import { supabase } from "../supabaseClient"

export const getSignedImageUrl = async (filePath) => {
  if (!filePath) return null

  const { data, error } = await supabase.storage
    .from('files')
    .createSignedUrl(filePath, 60 * 60) // URL válida por 1 hora

  if (error) {
    console.error('Error al generar URL firmada:', error)
    return null
  }

  return data.signedUrl
}

export const uploadFileToStorage = async (bucket, path, file) => {
  if (!file || !bucket || !path) {
    throw new Error('Faltan parámetros obligatorios para la subida de archivo.');
  }

  const { error } = await supabase
    .storage
    .from(bucket)
    .upload(path, file, {
      upsert: false, // evitar sobrescribir archivos
      cacheControl: '3600'
    });

  if (error) {
    console.error('Error al subir archivo a storage:', error.message);
    throw new Error('Error al subir archivo');
  }

  return path;
};

export const uploadDocument = async ({ file, officeId, caseId, taskId, uploadedBy }) => {
  try {
    // Validaciones
    if (!file) {
      throw new Error('No se ha seleccionado ningún archivo');
    }

    if (!officeId || !caseId) {
      throw new Error('Faltan datos requeridos (officeId, caseId)');
    }

    // Validar tamaño del archivo (máximo 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      throw new Error('El archivo es demasiado grande. Máximo permitido: 50MB');
    }

    // Validar tipo de archivo (opcional - puedes ajustar según tus necesidades)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/plain',
      'text/csv'
    ];

    if (file.type && !allowedTypes.includes(file.type)) {
      console.warn(`Tipo de archivo no validado: ${file.type}. Continuando...`);
    }

    // Crear FormData para enviar a la edge function
    const formData = new FormData();
    formData.append('file', file);
    formData.append('case_id', caseId);
    formData.append('office_id', officeId);
    formData.append('fileName', file.name);
    
    // AGREGAR: Soporte para task_id
    if (taskId) {
      formData.append('task_id', taskId);
    }
    
    if (uploadedBy) {
      formData.append('created_by', uploadedBy);
    }

    console.log('Enviando archivo a edge function...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      caseId,
      officeId,
      taskId: taskId || 'N/A' // AGREGAR
    });

    // Llamar a la edge function
    const { data, error } = await supabase.functions.invoke('document-upload-google-drive', {
      body: formData
    });

    if (error) {
      console.error('Error en edge function:', error);
      throw new Error(`Error al subir archivo: ${error.message}`);
    }

    if (!data || !data.success) {
      console.error('Respuesta de error desde edge function:', data);
      throw new Error(data?.error || 'Error desconocido al subir archivo');
    }

    console.log('Archivo subido exitosamente:', data);

    return {
      success: true,
      document: data.document,
      case_info: data.case_info
    };

  } catch (error) {
    console.error('Error en uploadDocument:', error);
    
    // Proporcionar mensajes de error más específicos
    let userMessage = error.message;
    
    if (error.message.includes('JWT')) {
      userMessage = 'Error de autenticación con Google Drive. Contacte al administrador.';
    } else if (error.message.includes('storage_config')) {
      userMessage = 'Configuración de almacenamiento no encontrada. Contacte al administrador.';
    } else if (error.message.includes('Caso no encontrado')) {
      userMessage = 'El expediente especificado no existe.';
    } else if (error.message.includes('demasiado grande')) {
      userMessage = error.message; // Ya es un mensaje claro
    }

    throw new Error(userMessage);
  }
};

// Función auxiliar para descargar documentos desde Google Drive
export const downloadDocument = async (documentId) => {
  try {
    const { data, error } = await supabase
      .from('document')
      .select('*')
      .eq('id', documentId)
      .single();

    if (error || !data) {
      throw new Error('Documento no encontrado');
    }

    if (!data.google_drive_id) {
      throw new Error('Este documento no tiene asociado un archivo en Google Drive');
    }

    // Generar URL de descarga directa
    const downloadUrl = `https://drive.google.com/uc?id=${data.google_drive_id}&export=download`;
    
    return {
      downloadUrl,
      document: data
    };

  } catch (error) {
    console.error('Error al obtener URL de descarga:', error);
    throw error;
  }
};

export const shareCaseDriveFolder = async (caseId) => {
  try {
    const { data, error } = await supabase.functions.invoke('share-case-drive-folder', {
      body: { case_id: caseId }
    });

    if (error) {
      console.error('Error al compartir carpeta de Drive del expediente:', error);
      return { success: false, error: error.message };
    }

    return data;
  } catch (error) {
    console.error('Error al compartir carpeta de Drive del expediente:', error);
    return { success: false, error: error.message };
  }
};

// Función para obtener información de un documento
export const getDocumentInfo = async (documentId) => {
  try {
    const { data, error } = await supabase
      .from('document')
      .select(`
        *,
        uploader:person!created_by(full_name)
      `)
      .eq('id', documentId)
      .single();

    if (error) {
      throw new Error('Error al obtener información del documento');
    }

    return data;
  } catch (error) {
    console.error('Error en getDocumentInfo:', error);
    throw error;
  }
};

// Guarda (upsert) la metadata capturada al subir un documento, consultada
// luego por el copiloto IA (search_documents) para evitar tener que leer
// el archivo completo. No hace nada si no se llenó ningún campo.
export const saveDocumentMetadata = async (documentId, caseId, metadata) => {
  const { docType, summary, involvedParties, keyAmount, keyDate, notes } = metadata;
  const hasData = [docType, summary, involvedParties, keyAmount, keyDate, notes].some(
    (v) => v !== null && v !== undefined && v !== ''
  );
  if (!documentId || !caseId || !hasData) return null;

  const { data, error } = await supabase
    .from('document_metadata')
    .upsert({
      document_id: documentId,
      case_id: caseId,
      doc_type: docType || null,
      summary: summary || null,
      involved_parties: involvedParties || null,
      key_amount: keyAmount === '' || keyAmount === null || keyAmount === undefined ? null : Number(keyAmount),
      key_date: keyDate || null,
      notes: notes || null,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('Error al guardar metadata del documento:', error);
    throw error;
  }
  return data;
};

// Función para formatear el tamaño del archivo
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};